import React, { useEffect, useRef } from 'react';

const getAddressComponent = (components, type) => {
  return (
    components.find((component) => component.types.includes(type))
      ?.long_name || ""
  );
};

const getAddressComponentShort = (components, type) => {
  return (
    components.find((component) => component.types.includes(type))
      ?.short_name || ""
  );
};

const parseGooglePlace = (place) => {
  const components = place.address_components || [];

  const streetNumber = getAddressComponent(components, "street_number");
  const route = getAddressComponent(components, "route");
  const subpremise = getAddressComponent(components, "subpremise");

  const city =
    getAddressComponent(components, "locality") ||
    getAddressComponent(components, "postal_town") ||
    getAddressComponent(components, "administrative_area_level_2") ||
    getAddressComponent(components, "sublocality") ||
    getAddressComponent(components, "neighborhood");

  const state =
    getAddressComponentShort(components, "administrative_area_level_1") ||
    getAddressComponent(components, "administrative_area_level_1");

  const postalCode = getAddressComponent(components, "postal_code");
  const country = getAddressComponent(components, "country");

  return {
    streetAddress: [streetNumber, route].filter(Boolean).join(" "),
    apartment: subpremise || "",
    city,
    state,
    postalCode,
    country,
  };
};

const GoogleMapsAutocomplete = ({
  inputRef,
  setAddressSearch,
  onPlaceSelected,
}) => {
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);

  const latestSetAddressSearchRef = useRef(setAddressSearch);
  const latestOnPlaceSelectedRef = useRef(onPlaceSelected);

  useEffect(() => {
    latestSetAddressSearchRef.current = setAddressSearch;
  }, [setAddressSearch]);

  useEffect(() => {
    latestOnPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    if (!window.google || !window.google.maps?.places || !inputRef.current) return;

    if (autocompleteRef.current) {
      return;
    }
    // Initialize the autocomplete on your existing textarea
    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        fields: ["address_components", "formatted_address", "place_id", "name"],
        types: ["address"],
      }
    );

    listenerRef.current = autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();

        if (!place) return;

        const hasComponents =
          Array.isArray(place.address_components) &&
          place.address_components.length > 0;

        const formattedAddress =
          place.formatted_address ||
          inputRef.current?.value ||
          "";

        if (!hasComponents) {
          latestSetAddressSearchRef.current(formattedAddress);
          return;
        }

        const parsedAddress = parseGooglePlace(place);

        if (inputRef.current) {
          inputRef.current.value = formattedAddress;
        }

        latestSetAddressSearchRef.current(formattedAddress);
        latestOnPlaceSelectedRef.current(parsedAddress, formattedAddress);
      });

    return () => {
      // Clean up listeners
      if (listenerRef.current) {
        window.google.maps.event.removeListener(listenerRef.current);
      }

      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }

      autocompleteRef.current = null;
      listenerRef.current = null;
    };
  }, [inputRef]);

  // IMPORTANT: Return null - don't render anything!
  return null;
};

export default GoogleMapsAutocomplete;
